import actionCreatorFactory from "typescript-fsa";
import { reducerWithInitialState } from "typescript-fsa-reducers";
import {
  all,
  call,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/database";
import appConfig from "../constants/appConfig";
import {
  fetchDatabaseValuesMap,
  fetchFirestoreCollectionMap,
  fetchFbTokenWithoutTokenRefresh,
  fetchFirestoreCollectionRaw,
  fetchFirestoreCollectionBySnapshot,
  setFirestoreDocument,
  fetchFirestoreDocument,
} from "../utility/firebase";
import { IsJsonString, convertMapToValues, getCookieValue } from "utility";
import { getNowTimestamp } from "../utility";
import { isVODTicket } from "../utility/event";
import { firestoreActions, isRegisteredChannel } from "./firestore";
import { eventChannel } from "redux-saga";
import type { DocumentChange, Timestamp } from "@google-cloud/firestore";
import { purchaseActions } from "./purchase";
import type { Store } from "store";
import { getCommentCollectionPath } from "utility/streaming";
import type { MyProductData } from "@spwn/types";
import type { ResGetStreamingKey } from "@spwn/types/functions";
import type {
  GiftItem,
  EventVideo,
  ProductData,
  UserMessage,
  EventChatInfo,
  MyEventTicketData,
} from "@spwn/types/firebase/firestore";
import type { PawItem } from "@spwn/types/firebase/database";

const actionCreator = actionCreatorFactory("streaming");

/* eslint-disable @typescript-eslint/no-explicit-any */
export const streamingActions = {
  setStateByKey: actionCreator<ReqSetStateByKey>("setStateByKey"),
  setCurrentVideoId: actionCreator<string | null>("setCurrentVideoId"),
  fetchCommentShardId: actionCreator.async<
    ReqFetchShardIdArgs | undefined,
    CommentShardId["value"]
  >("fetchCommentShardId"),
  clearStateByKey: actionCreator<keyof streamingState>("clearStateByKey"),
  fetchGiftItems: actionCreator.async<ReqFetchGiftItems, any>("fetchGiftItems"),
  getStreamingKey: actionCreator.async<ReqGetStreamingKey, any>(
    "getStreamingKey"
  ),
  watchEventVideos: actionCreator.async<ReqWatchEventVideos, any>(
    "watchEventVideos"
  ),
  createMyVideos: actionCreator.async<MyEventTicketData[], any>(
    "createMyVideos"
  ), // create my video data from myTickets
  watchSuperChat: actionCreator.async<ReqWatchSuperChat, any>("watchSuperChat"),
  createMySmallIconUrl: actionCreator.async<void, any>("createMySmallIconUrl"),
  fetchDisplayComments: actionCreator.async<ReqFetchDisplayComments, any>(
    "fetchDisplayComments"
  ),
  newFetchDisplayComments: actionCreator.async<
    ReqFetchDisplayComments,
    UserMessage[]
  >("newFetchDisplayComments"),
  fetchBANComments: actionCreator.async<ReqFetchBANComments, any>(
    "fetchBANComments"
  ),
  handleSeekEvents: actionCreator.async<ReqHandleSeekEvents, any>(
    "handleSeekEvents"
  ),
  fetchDisplayCommentsByTimeUpdate: actionCreator.async<
    ReqFetchDisplayCommentsByTimeUpdate,
    any
  >("fetchDisplayCommentsByTimeUpdate"),
  deleteDisplayComment: actionCreator<string>("deleteComment"),
  setLastCommentedSeconds: actionCreator<number>("setLastCommentedSeconds"),
  setLastCommentedTimestamp: actionCreator<Timestamp>(
    "setLastCommentedTimestamp"
  ),
  setIsCommentDisabled: actionCreator<boolean>("setIsCommentDisabled"),
  getStreamingSettings: actionCreator<void>("getStreamingSettings"),
  toggleDarkMode: actionCreator<void>("toggleDarkMode"),
  toggleTheaterMode: actionCreator<void>("toggleTheaterMode"),
  toggleOnlyChatMode: actionCreator<void>("toggleOnlyChatMode"),
  postComment: actionCreator.async<ReqPostComment, UserMessage>("postComment"),
  updateLiveCommerceCart: actionCreator<{
    item: ProductData;
    count: number;
    isRemove?: boolean;
  }>("updateLiveCommerceCart"),
  successPurchaseLiveCommerce: actionCreator<
    streamingState["liveCommerce"]["isPurchased"]
  >("successPurchaseLiveCommerce"),
  setPhonePurchaseUrl: actionCreator<
    streamingState["liveCommerce"]["phonePurchaseUrl"]
  >("setPhonePurchaseUrl"),
  toggleLiveCommerceError: actionCreator<
    streamingState["liveCommerce"]["errorInfo"]
  >("toggleLiveCommerceError"),
  fetchNgWords: actionCreator.async<ReqNgWords, any>("fetchNgWords"),
  fetchBanUserIds: actionCreator.async<ReqFetchBANComments, any>(
    "fetchBanUserIds"
  ),
  bufferedCommentPut: actionCreator<{
    isLocalChange: boolean;
    docChanges: DocumentChange[];
  }>("bufferedCommentPut"),
  bufferedCommentClear: actionCreator("bufferedCommentClear"),
  startCommentPolling: actionCreator.async<
    ReqFetchDisplayComments,
    UserMessage[]
  >("startCommentPolling"),
  stopCommentPolling: actionCreator("stopCommentPolling"),
  setPauseCommentFetch: actionCreator<boolean>("setPauseCommentFetch"),
};
/* eslint-enable @typescript-eslint/no-explicit-any */

type CommentShardId =
  // 初期化
  | { type: "init"; value: undefined }
  // shardingしていて部屋番号割り当てられている
  // nullはshardingしていない
  | { type: "loaded"; value: number | null };

export interface streamingState {
  giftItemMap: { [key: string]: GiftItemData };
  streamingKey: ResGetStreamingKey;
  streamingType: StreamingType;
  currentVideoId: string | null;
  eventVideoMap: { [videoId: string]: EventVideo } | null;
  myVideoInfo: {
    myVideoMap: { [eventId: string]: MyEventTicketData };
    videoDataMap: { [videoId: string]: EventVideo };
  };
  superChatList: UserMessage[] | null;
  iconUrls: {
    "32": string;
    "64": string;
    "128": string;
  } | null;
  bufferedComments: DocumentChange[]; // 更新待機中のバッファされたコメント
  displayComments: UserMessage[];
  reservedComments: UserMessage[]; // for VOD streaming
  lastCommentedSeconds: number;
  lastCommentedTimestamp: Timestamp | undefined;
  isPollingMode: boolean;
  pauseCommentFetch: boolean;
  commentShardId: CommentShardId;
  isCommentDisabled: boolean;
  streamingSettings: {
    isDarkModeEnabled: boolean;
    isTheaterModeEnabled: boolean;
    isOnlyChatEnabled: boolean;
    ngWords: string[];
    banUserIds: string[] | null;
  };
  liveCommerce: {
    cartItemMap: { [pid: string]: ProductData & { count: number } };
    cartTotalNum: number;
    isPurchased: boolean;
    phonePurchaseUrl: string;
    errorInfo: {
      isError: boolean;
      caption?: string;
      msg: string;
    };
  };
}

export type GiftItemData = GiftItem & {
  type: string;
  values: {
    free: number;
    paid: number;
  };
};

export type StreamingType = "Live" | "VOD";

export type ActionTabType =
  | "eventDetail"
  | "comment"
  | "goods"
  | "event"
  | null; // pc: comment only now

export type CommentFilterType = "all" | "comment" | "gift" | "none" | null;

export type ReqSetStateByKey = {
  [P in keyof streamingState]?: streamingState[P];
};

export interface ReqFetchGiftItems {
  eid: string;
}
export interface ReqGetStreamingKey {
  eid: string;
}

type ReqWatchEventVideos = {
  eventId: string;
};

interface ReqWatchSuperChat {
  eid: string;
  vid: string;
}

type ReqFetchDisplayComments = {
  eventId: string;
  videoId: string;
};

type ReqFetchBANComments = {
  eventId: string;
  videoId: string;
};

type ReqHandleSeekEvents = {
  eventId: string;
  videoId: string;
  player: { currentTime: number }; // theoplayer
  eventStart: Timestamp;
  removeChannel?: boolean;
  isEdge?: boolean;
};

type ReqFetchDisplayCommentsByTimeUpdate = {
  eventId: string;
  videoId: string;
  currentTime: number;
  eventStart: Timestamp;
};

export type ReqPostComment = {
  eventId: string;
  videoId: string;
  isGift: boolean;
  itemId: string; // for usePAW
  postData: Omit<UserMessage, "timestamp">;
  mutatePAW?: () => Promise<void>;
};

type ReqNgWords = {
  eventId: string;
};

const initialState: streamingState = {
  // @ts-expect-error TS2322
  giftItemMap: null,
  // @ts-expect-error TS2322
  streamingKey: null,
  streamingType: "Live",
  currentVideoId: null,
  eventVideoMap: null,
  // @ts-expect-error TS2322
  myVideoInfo: null,
  superChatList: null,
  iconUrls: null,
  bufferedComments: [],
  displayComments: [],
  reservedComments: [],
  lastCommentedSeconds: 0,
  isPollingMode: false,
  commentShardId: { type: "init", value: undefined },
  isCommentDisabled: false,
  streamingSettings: {
    isDarkModeEnabled: true, // デフォルトでダークモードを有効にする
    isTheaterModeEnabled: false,
    isOnlyChatEnabled: false,
    ngWords: [],
    banUserIds: null,
  },
  liveCommerce: {
    // @ts-expect-error TS2322
    cartItemMap: null,
    cartTotalNum: 0,
    isPurchased: false,
    // @ts-expect-error TS2322
    phonePurchaseUrl: null,
    errorInfo: {
      isError: false,
      caption: "",
      msg: "",
    },
  },
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const streamingReducer = reducerWithInitialState(initialState)
  .case(streamingActions.setStateByKey, (state, payload) => {
    const [key] = Object.keys(payload);
    // @ts-expect-error TS7053
    return { ...state, [key]: payload[key] };
  })
  .case(streamingActions.setCurrentVideoId, (state, currentVideoId) => {
    return { ...state, currentVideoId };
  })
  // @ts-expect-error TS2345
  .case(streamingActions.fetchCommentShardId.done, (state, payload) => {
    return {
      ...state,
      commentShardId: { type: "loaded", value: payload.result },
    };
  })
  .case(
    streamingActions.clearStateByKey,
    (state, key: keyof streamingState) => {
      return { ...state, [key]: initialState[key] };
    }
  )
  .case(streamingActions.fetchGiftItems.done, (state, payload: any) => {
    return { ...state, giftItemMap: payload };
  })
  .case(streamingActions.getStreamingKey.done, (state, payload: any) => {
    return { ...state, streamingKey: payload };
  })
  .case(streamingActions.watchEventVideos.done, (state, payload: any) => {
    return { ...state, eventVideoMap: payload };
  })
  .case(streamingActions.createMyVideos.done, (state, payload: any) => {
    return { ...state, myVideoInfo: payload };
  })
  .case(streamingActions.watchSuperChat.done, (state, payload: any) => {
    return { ...state, superChatList: payload };
  })
  .case(streamingActions.createMySmallIconUrl.done, (state, payload: any) => {
    return { ...state, iconUrls: payload };
  })
  .case(streamingActions.fetchDisplayComments.started, (state, _) => {
    return { ...state, isPollingMode: false, pauseCommentFetch: false };
  })
  .case(streamingActions.fetchDisplayComments.done, (state, payload: any) => {
    return { ...state, displayComments: payload };
  })
  .case(streamingActions.newFetchDisplayComments.started, (state, _) => {
    return { ...state, isPollingMode: false, pauseCommentFetch: false };
  })
  .case(streamingActions.newFetchDisplayComments.done, (state, payload) => {
    return { ...state, displayComments: payload.result };
  })
  .case(streamingActions.fetchBANComments.done, (state, payload: any) => {
    return { ...state, displayComments: payload };
  })
  .case(streamingActions.handleSeekEvents.started, (state) => {
    return { ...state, pauseCommentFetch: false };
  })
  .case(streamingActions.handleSeekEvents.done, (state, payload: any) => {
    return {
      ...state,
      displayComments: payload,
    };
  })
  .case(
    streamingActions.fetchDisplayCommentsByTimeUpdate.done,
    (state, payload: any) => {
      return { ...state, reservedComments: payload };
    }
  )
  .case(streamingActions.deleteDisplayComment, (state, commentId: string) => {
    const { displayComments } = state;
    // remove comment from displayComments
    const index = displayComments.findIndex(
      (comment) => comment._id === commentId
    );
    if (index !== -1) {
      displayComments.splice(index, 1);
    }
    return {
      ...state,
      displayComments: displayComments.slice(-DISPLAY_COMMENT_NUM),
    };
  })
  .case(streamingActions.setLastCommentedSeconds, (state, timestamp) => {
    return { ...state, lastCommentedSeconds: timestamp };
  })
  .case(streamingActions.setLastCommentedTimestamp, (state, timestamp) => {
    return { ...state, lastCommentedTimestamp: timestamp };
  })
  .case(streamingActions.setIsCommentDisabled, (state, isCommentDisabled) => {
    return { ...state, isCommentDisabled };
  })
  .case(streamingActions.getStreamingSettings, (state) => {
    // get straeming setting(dark mode) from cookie
    // Cookieが"false"の場合のみライトモードにし、それ以外はダークモードをデフォルトとする
    const isDarkModeEnabled = getCookieValue("preferDarkMode") !== "false";
    return {
      ...state,
      streamingSettings: { ...state.streamingSettings, isDarkModeEnabled },
    };
  })
  .case(streamingActions.toggleDarkMode, (state) => {
    const isDarkModeEnabled = !state.streamingSettings.isDarkModeEnabled;
    document.cookie = `preferDarkMode=${isDarkModeEnabled}`;
    return {
      ...state,
      streamingSettings: { ...state.streamingSettings, isDarkModeEnabled },
    };
  })
  .case(streamingActions.toggleTheaterMode, (state) => {
    const isTheaterModeEnabled = !state.streamingSettings.isTheaterModeEnabled;
    return {
      ...state,
      streamingSettings: { ...state.streamingSettings, isTheaterModeEnabled },
    };
  })
  .case(streamingActions.toggleOnlyChatMode, (state) => {
    const isOnlyChatEnabled = !state.streamingSettings.isOnlyChatEnabled;
    return {
      ...state,
      streamingSettings: { ...state.streamingSettings, isOnlyChatEnabled },
    };
  })
  .case(streamingActions.postComment.done, (state, payload) => {
    if (!state.isPollingMode) {
      return state;
    }

    // ポーリング時はonSnapshot経由で即時反映できないので、ここで追加する
    return {
      ...state,
      displayComments: [
        ...state.displayComments,
        { ...payload.result, isLocalChange: true },
      ],
    };
  })
  .case(streamingActions.updateLiveCommerceCart, (state, payload) => {
    const { item, count, isRemove } = payload;
    const itemId = item._id;
    const currentCartItem = { ...(state.liveCommerce.cartItemMap ?? {}) };

    if (!itemId) {
      console.error("itemId is required to update cart");
      return state;
    }

    if (isRemove) {
      delete currentCartItem[itemId];
    } else if (!currentCartItem || !currentCartItem[itemId]) {
      currentCartItem[itemId] = {
        ...item,
        count,
      };
    } else {
      if (currentCartItem[itemId].count + count <= 0) {
        currentCartItem[itemId].count = 0;
      } else {
        currentCartItem[itemId].count += count;
      }
    }

    const cartItems = convertMapToValues(currentCartItem);

    const cartTotalNum =
      cartItems.length === 0
        ? 0
        : cartItems.map((el) => el.count).reduce((a, b) => a + b);
    return {
      ...state,
      liveCommerce: {
        ...state.liveCommerce,
        cartItemMap: currentCartItem,
        cartTotalNum,
      },
    };
  })
  .case(streamingActions.successPurchaseLiveCommerce, (state, isPurchased) => {
    // if success, clear cart
    const liveCommerce = isPurchased
      ? { ...initialState.liveCommerce, isPurchased }
      : { ...state.liveCommerce, isPurchased };
    return { ...state, liveCommerce };
  })
  .case(streamingActions.setPhonePurchaseUrl, (state, phonePurchaseUrl) => {
    return {
      ...state,
      liveCommerce: {
        ...state.liveCommerce,
        phonePurchaseUrl,
      },
    };
  })
  .case(streamingActions.toggleLiveCommerceError, (state, errorInfo) => {
    const liveCommerce = { ...state.liveCommerce, errorInfo };
    return { ...state, liveCommerce };
  })
  .case(streamingActions.fetchNgWords.done, (state, payload: any) => {
    return {
      ...state,
      streamingSettings: { ...state.streamingSettings, ngWords: payload },
    };
  })
  .case(streamingActions.fetchBanUserIds.done, (state, payload) => {
    return {
      ...state,
      streamingSettings: {
        ...state.streamingSettings,
        banUserIds: payload.result,
      },
    };
  })
  // onSnapshotからの更新をバッファーに追加
  .case(streamingActions.bufferedCommentPut, (state, payload): any => {
    // コメント投稿した本人には即時反映させる
    if (payload.isLocalChange) {
      return {
        ...state,
        displayComments: state.displayComments.concat(
          payload.docChanges
            .filter((change) => change.type === "added")
            .map((change) => ({
              ...(change.doc.data() as UserMessage),
              _id: change.doc.id,
            }))
        ),
      };
    }

    return {
      ...state,
      bufferedComments: state.bufferedComments.concat(payload.docChanges),
    };
  })
  .case(streamingActions.bufferedCommentClear, (state) => {
    return {
      ...state,
      bufferedComments: [],
    };
  })
  .case(streamingActions.startCommentPolling.started, (state, _) => {
    return { ...state, isPollingMode: true };
  })
  .case(streamingActions.startCommentPolling.done, (state, payload) => {
    return {
      ...state,
      displayComments: payload.result,
      pauseCommentFetch: false,
    };
  })
  .case(streamingActions.stopCommentPolling, (state) => {
    return { ...state, isPollingMode: false };
  })
  .case(streamingActions.setPauseCommentFetch, (state, pauseCommentFetch) => {
    return { ...state, pauseCommentFetch };
  });
/* eslint-enable @typescript-eslint/no-explicit-any */

export default streamingReducer;

export function* streamingSaga() {
  yield takeEvery(
    // @ts-expect-error TS2769
    streamingActions.fetchCommentShardId.started,
    fetchCommentShardId
  );
  yield takeEvery(streamingActions.fetchGiftItems.started, fetchGiftItems);
  yield takeEvery(streamingActions.getStreamingKey.started, getStreamingKey);
  yield takeEvery(streamingActions.watchEventVideos.started, watchEventVideos);
  yield takeEvery(streamingActions.createMyVideos.started, createMyVideos);
  yield takeEvery(streamingActions.watchSuperChat.started, watchSuperChat);
  yield takeEvery(
    streamingActions.createMySmallIconUrl.started,
    createMySmallIconUrl
  );
  yield takeEvery(
    streamingActions.fetchDisplayComments.started,
    fetchDisplayComments
  );
  yield takeEvery(streamingActions.fetchBANComments.started, fetchBANComments);
  yield takeEvery(streamingActions.handleSeekEvents.started, handleSeekEvents);
  yield takeEvery(
    streamingActions.fetchDisplayCommentsByTimeUpdate.started,
    fetchDisplayCommentsByTimeUpdate
  );
  yield takeEvery(streamingActions.postComment.started, postComment);
  yield takeEvery(streamingActions.fetchNgWords.started, fetchNgWords);
  yield takeLatest(streamingActions.fetchBanUserIds.started, fetchBanUserIds);
  yield takeLatest(
    streamingActions.newFetchDisplayComments.started,
    handleCommentFlowSaga
  );
  yield takeLatest(
    streamingActions.startCommentPolling.started,
    watchPollingComment
  );
}

/**
 * コメント関連のメインのフローを管理するSaga
 * newFetchDisplayComments.startedアクションからIDを受け取り、2つのワーカーSagaを起動する
 */
function* handleCommentFlowSaga(
  action: ReturnType<typeof streamingActions.newFetchDisplayComments.started>
) {
  const { eventId, videoId } = action.payload;

  const {
    event: { displayEvent },
    streaming: { commentShardId },
  }: Store = yield select();

  try {
    const msgRef = getCommentCollectionPath(
      eventId,
      videoId,
      displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
      commentShardId.value
    );

    // forkで2つのSagaを並列実行する
    // allで囲むことで、どちらかでエラーが発生した場合に両方を停止させやすくする
    yield all([fork(watchOnSnapshotSaga, msgRef), fork(processBatchesSaga)]);
  } catch (e) {
    console.error(e);
  }
}

function* fetchGiftItems(action: { payload: ReqFetchGiftItems }) {
  const { eid } = action.payload;
  if (!eid) {
    return;
  }
  try {
    const masterDataItems: {
      [key: string]: PawItem;
    } = yield fetchDatabaseValuesMap<PawItem>(`/items`);
    const giftMap: {
      [key: string]: GiftItemData;
    } = yield fetchFirestoreCollectionMap(`/chat/${eid}/gifts`);
    Object.keys(giftMap).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const gift = giftMap[key]!;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const data = masterDataItems[gift.itemId]!;
      giftMap[key] = {
        ...gift,
        type: data.type,
        values: data.values,
      };
    });
    yield put(streamingActions.fetchGiftItems.done(giftMap as never));
  } catch (e) {
    console.error(e);
  }
}

function* getStreamingKey(action: { payload: ReqGetStreamingKey }) {
  try {
    const fbToken: string | null = yield fetchFbTokenWithoutTokenRefresh();
    if (!fbToken) {
      const data: Partial<ResGetStreamingKey> = { isError: true };
      yield put(streamingActions.getStreamingKey.done(data as never));
      return;
    }
    const response: Response = yield fetch(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      appConfig.CloudFunctions.getStreamingKey!,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `'Bearer ${fbToken}`,
        },
        body: JSON.stringify({
          eid: action.payload.eid,
        }),
      }
    );
    if (response.status === 200) {
      const json: ResGetStreamingKey = yield response.json();
      yield put(streamingActions.getStreamingKey.done(json as never));
    } else {
      const data: Partial<ResGetStreamingKey> = { isError: true };
      yield put(streamingActions.getStreamingKey.done(data as never));
    }
  } catch (e) {
    console.error(e);
    const data: Partial<ResGetStreamingKey> = { isError: true };
    yield put(streamingActions.getStreamingKey.done(data as never));
  }
}

function videosChannel(eventId: string) {
  return eventChannel((emitter) => {
    const unsubscribe = firebase
      .firestore()
      .collection(`streaming/${eventId}/videos`)
      .onSnapshot(
        (snapshot) => {
          let map = {};
          snapshot.forEach((doc) => {
            map = { ...map, [doc.id]: { ...doc.data(), _id: doc.id } };
          });
          emitter(map);
        },
        (error) => {
          console.error(error);
        }
      );
    return unsubscribe;
  });
}
function* watchEventVideos(action: { payload: ReqWatchEventVideos }) {
  const { eventId } = action.payload;
  const {
    firestore: { channels },
  }: Store = yield select();
  if (isRegisteredChannel(channels, "eventVideos")) return;
  try {
    // @ts-expect-error TS7057
    const channel = yield call(videosChannel, eventId);
    yield put(firestoreActions.addChannel({ ...channel, name: "eventVideos" }));
    while (true) {
      // @ts-expect-error TS7057
      const data = yield take(channel);
      yield put(streamingActions.watchEventVideos.done(data));
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * create only viewable video data
 * !!! call only from fetchMyTickets !!!
 * @param action
 */
function* createMyVideos(action: { payload: MyEventTicketData[] }) {
  const myTickets = action.payload;

  let myVideoMap = {};
  const videoPromises = [];
  for (const myTicketInfo of myTickets) {
    // get vod ticket (unpurchased / not expired)
    const vodTickets = myTicketInfo.tickets.filter(
      (el) =>
        isVODTicket(el) &&
        (el.status === "UNPROCESS" ||
          getNowTimestamp() < getNowTimestamp(el.vodExpiredAt))
    );
    if (vodTickets.length === 0) {
      continue;
    }
    const eid = myTicketInfo.event._id;
    myVideoMap = {
      ...myVideoMap,
      // @ts-expect-error TS2464
      [eid]: {
        ...myTicketInfo,
        tickets: vodTickets,
      },
    };
    // fetch master video data
    videoPromises.push(
      fetchFirestoreCollectionMap("streaming/" + eid + "/videos")
    );
  }

  // create master video data
  // @ts-expect-error TS7057
  const promiseRootList = yield Promise.all(videoPromises);
  let videoDataMap: { [pid: string]: MyProductData } = {};
  for (let i = 0; i < promiseRootList.length; i++) {
    const element = promiseRootList[i];
    videoDataMap = { ...videoDataMap, ...element };
  }

  yield put(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    streamingActions.createMyVideos.done({ myVideoMap, videoDataMap } as any)
  );
}

function* watchSuperChat(action: { payload: ReqWatchSuperChat }) {
  const {
    firestore: { channels },
    event: { displayEvent },
    streaming: { commentShardId },
  }: Store = yield select();
  if (isRegisteredChannel(channels, "superChat")) return;
  try {
    const msgRef = getCommentCollectionPath(
      action.payload.eid,
      action.payload.vid,
      displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
      commentShardId.value
    );
    // @ts-expect-error TS7057
    const channel = yield call(superChatChannel, msgRef);
    while (true) {
      // @ts-expect-error TS7057
      const data = yield take(channel);
      yield put(streamingActions.watchSuperChat.done(data));
      yield put(firestoreActions.addChannel({ ...channel, name: "superChat" }));
    }
  } catch (e) {
    console.error(e);
  }
}

function superChatChannel(msgRef: string) {
  return eventChannel((emitter) => {
    const unsubscribe = firebase
      .firestore()
      .collection(msgRef)
      .where("isSuperChat", "==", true)
      .orderBy("timestamp")
      .onSnapshot(
        { includeMetadataChanges: true },
        (doc) => {
          if (!doc.metadata.hasPendingWrites) {
            // @ts-expect-error TS7034
            const commentList = [];
            doc.forEach((doc) => {
              commentList.push({ ...doc.data(), _id: doc.id });
            });
            // @ts-expect-error TS7005
            emitter(commentList);
          }
        },
        (error) => {
          console.error(error);
        }
      );
    return unsubscribe;
  });
}

function* createMySmallIconUrl() {
  let isError = true;
  try {
    const fbToken: string | null = yield fetchFbTokenWithoutTokenRefresh();
    const response: Response = yield fetch(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      appConfig.CloudFunctions.createMySmallIconUrl!,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `'Bearer ${fbToken}`,
        },
        body: JSON.stringify({}),
      }
    );
    // success
    if (response.status === 200) {
      // @ts-expect-error TS7057
      const json = yield response.json();
      yield put(streamingActions.createMySmallIconUrl.done(json.urls));
      isError = false;
      return;
    }
    const text: string = yield response.text();
    if (IsJsonString(text)) {
      console.error(JSON.parse(text).msg);
    } else {
      console.error(text);
    }
  } catch (e) {
    console.error(e);
  } finally {
    // failed
    if (isError) {
      const unknownIconUrls = {
        "32": "https://public.spwn.jp/utils/spwn/imgs/icon_profile.svg",
        "64": "https://public.spwn.jp/utils/spwn/imgs/icon_profile.svg",
        "128": "https://public.spwn.jp/utils/spwn/imgs/icon_profile.svg",
      };
      yield put(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        streamingActions.createMySmallIconUrl.done(unknownIconUrls as any)
      );
    }
  }
}

const COMMENT_RECEIVER_SIZE = 1;
const DISPLAY_COMMENT_NUM = 70;
const NEW_DISPLAY_COMMENT_NUM = 100;
const ARCHIVE_COMMENT_FETCH_SIZE = 100;

function streamingCommentChannel(msgRef: string) {
  return eventChannel((emitter) => {
    // !!FIXME
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let unsubscribe: any = fetchFirestoreCollectionRaw(msgRef);
    unsubscribe = unsubscribe.orderBy("timestamp", "desc");
    unsubscribe = unsubscribe.limit(COMMENT_RECEIVER_SIZE);
    unsubscribe = unsubscribe.onSnapshot(
      { includeMetadataChanges: true },
      // @ts-expect-error TS7006
      (doc) => {
        // ref: https://qiita.com/subaru44k/items/a88e638333b8d5cc29f2#%E5%A4%89%E6%9B%B4%E7%99%BA%E7%94%9F%E6%99%82%E3%81%AE%E9%80%9A%E7%9F%A5
        // `doc.metadata.hasPendingWrites ? "Local" : "Server";`
        emitter({
          isLocalChange: doc.metadata.hasPendingWrites,
          docChanges: doc.docChanges(),
        });
      }
    );
    return unsubscribe;
  });
}

function newStreamingCommentChannel(msgRef: string) {
  return eventChannel((emitter) => {
    return fetchFirestoreCollectionRaw(msgRef)
      .orderBy("timestamp", "desc")
      .limit(NEW_DISPLAY_COMMENT_NUM)
      .onSnapshot({ includeMetadataChanges: true }, (doc) => {
        // ref: https://qiita.com/subaru44k/items/a88e638333b8d5cc29f2#%E5%A4%89%E6%9B%B4%E7%99%BA%E7%94%9F%E6%99%82%E3%81%AE%E9%80%9A%E7%9F%A5
        // `doc.metadata.hasPendingWrites ? "Local" : "Server";`
        emitter({
          isLocalChange: doc.metadata.hasPendingWrites,
          docChanges: doc.docChanges(),
        });
      });
  });
}

/**
 * fetch all streaming comments first time
 */
function* fetchDisplayComments(action: { payload: ReqFetchDisplayComments }) {
  const { eventId, videoId } = action.payload;

  //既にlisten済ならclose
  const {
    firestore: { channels },
    event: { displayEvent },
    streaming: { commentShardId },
  }: Store = yield select();
  if (isRegisteredChannel(channels, "streamingComments")) {
    yield put(firestoreActions.closeChannel({ channel: "streamingComments" }));
  }
  try {
    const msgRef = getCommentCollectionPath(
      eventId,
      videoId,
      displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
      commentShardId.value
    );
    const snapshot = firebase
      .firestore()
      .collection(msgRef)
      .orderBy("timestamp", "desc")
      .limit(DISPLAY_COMMENT_NUM)
      .get();
    const newArray: UserMessage[] =
      yield fetchFirestoreCollectionBySnapshot<UserMessage>(snapshot);
    const comments = newArray.length > 0 ? newArray.reverse() : [];
    yield put(streamingActions.fetchDisplayComments.done(comments as never));

    // @ts-expect-error TS7057
    const channel = yield call(streamingCommentChannel, msgRef);
    yield put(
      firestoreActions.addChannel({ ...channel, name: "streamingComments" })
    );
    while (true) {
      // @ts-expect-error TS7057
      const data = yield take(channel);
      const { isLocalChange } = data;
      const { docChanges } = data;
      const {
        streaming: { displayComments, lastCommentedSeconds, pauseCommentFetch },
      }: Store = yield select();

      if (pauseCommentFetch) {
        continue;
      }

      // @ts-expect-error TS7006
      docChanges.forEach((change) => {
        if (
          change.type === "added" &&
          !displayComments.some((comment) => comment._id === change.doc.id)
        ) {
          // added かつ重複idでなければ追加
          displayComments.push({
            ...(change.doc.data() as UserMessage),
            _id: change.doc.id,
          });
        } else if (change.type === "modified") {
          // 自分の書き込みの場合は`modified`を通る
          const index = displayComments.findIndex(
            (comment) => comment._id === change.doc.id
          );
          if (index === -1) {
            // modified かつ重複idでなければ追加
            displayComments.push({
              ...(change.doc.data() as UserMessage),
              _id: change.doc.id,
            });
          } else {
            // modified かつ重複idなら同じidに上書き
            displayComments.splice(index, 1, {
              ...(change.doc.data() as UserMessage),
              _id: change.doc.id,
            });
          }
        }
        // `change.type: removed` はonSnapshotのlimit対象から外れたドキュメントのことも指すため削除されたコメントとは限らない
        // ここでは判定できないので何もしない。 ref: https://qiita.com/mktu/items/17a993f675ccd6d17aed
      });
      // コメント描画負荷対策として、1秒間に1度の描画になるようにコメント取得の発火を一部無視する
      const unixTimestamp = getNowTimestamp();
      if (isLocalChange || lastCommentedSeconds < unixTimestamp) {
        yield put(
          streamingActions.fetchDisplayComments.done(
            displayComments.slice(-DISPLAY_COMMENT_NUM) as never
          )
        );
        yield put(streamingActions.setLastCommentedSeconds(unixTimestamp));
      }
    }
  } catch (e) {
    console.error(e);
  }
}

type StreamingBANCommentChannelEmitInput = {
  isLocalChange: boolean;
  docChanges: firebase.firestore.DocumentChange<firebase.firestore.DocumentData>[];
};
function streamingBANCommentChannel(msgRef: string) {
  return eventChannel((emitter) => {
    const unsubscribe = firebase
      .firestore()
      .collection(msgRef)
      .where("isBan", "==", true)
      // NOTE: banReason: ngWord なコメントは最初からBANされているのでwatchする必要がない
      //       将来的にリアルタイムでNGワードを変更したくなったら要変更
      .where("banReason", "!=", "ngWord")
      .orderBy("banReason")
      .orderBy("timestamp", "desc")
      // NOTE: DVR/VODでは isBan: true なコメントが DISPLAY_COMMENT_NUM
      //       より多いと即時反映出来ない状態だったので、limitを外す事で対応。
      // .limit(DISPLAY_COMMENT_NUM) // 表示分のみ取得
      .onSnapshot((docs) => {
        emitter({
          isLocalChange: docs.metadata.hasPendingWrites,
          docChanges: docs.docChanges(),
        } satisfies StreamingBANCommentChannelEmitInput);
      });
    return unsubscribe;
  });
}

/**
 * use for detection deleted comment
 * @param action
 */
function* fetchBANComments(action: { payload: ReqFetchBANComments }) {
  const { eventId, videoId } = action.payload;
  const {
    firestore: { channels },
    auth: { user },
    event: { displayEvent },
    streaming: { commentShardId },
  }: Store = yield select();
  if (isRegisteredChannel(channels, "streamingBANComments")) return;
  try {
    const msgRef = getCommentCollectionPath(
      eventId,
      videoId,
      displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
      commentShardId.value
    );
    // @ts-expect-error TS7057
    const channel = yield call(streamingBANCommentChannel, msgRef);
    yield put(
      firestoreActions.addChannel({ ...channel, name: "streamingBANComments" })
    );
    while (true) {
      try {
        const {
          isLocalChange,
          docChanges,
        }: StreamingBANCommentChannelEmitInput = yield take(channel);
        const {
          streaming: { displayComments, isPollingMode },
        }: Store = yield select();
        /**
         * @description
         * 取得したコメント数が上限を超えている場合、docChangesは"add"と"remove"の両方が含まれる。
         * その場合、表示するコメントを作成する処理がスキップされ、コメントの削除が正しく反映されないためCOMMENT_RECEIVER_SIZEを2にしている。
         * COMMENT_RECEIVER_SIZEを直接変更すると既存実装に影響があるので直接"2"を入れる。
         * この処理を入れた理由が不明なのと、この処理を削除するとコメント削除が反映されなくなる問題が発生するため、処理自体は残している。
         * @see https://github.com/balus-co-ltd/spwn/pull/11036
         */
        if (docChanges.length > 2) {
          // HOTFIX: if first fetch, ignore
          continue;
        }
        docChanges.forEach((change) => {
          // ignore permission denied update. localチェックがないと一瞬消えて再表示される。
          if (!isLocalChange && change.type === "added") {
            // remove ban comment from displayComments
            const index = displayComments.findIndex(
              (comment) => comment._id === change.doc.id
            );
            if (index === -1) return;
            if (displayComments[index]?.uid === user.uid) {
              // exclude own comment
              return;
            }

            if (!isPollingMode) {
              displayComments.splice(index, 1);
            } else {
              // 新プレイヤー + polling の時だけBANされたコメントが削除されましたメッセージに変わる
              displayComments.splice(index, 1, {
                ...(change.doc.data() as UserMessage),
                _id: change.doc.id,
              });
            }
          }
        });
        yield put(
          streamingActions.fetchBANComments.done(
            displayComments.slice(-DISPLAY_COMMENT_NUM) as never
          )
        );
      } catch (e) {
        console.error(e);
      }
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * trigger by seek in VOD streming
 * @param action
 */
function* handleSeekEvents(action: { payload: ReqHandleSeekEvents }) {
  try {
    // seek時に監視を外す
    const {
      firestore: { channels },
    }: Store = yield select();
    if (
      isRegisteredChannel(channels, "streamingComments") &&
      action.payload.removeChannel
    ) {
      console.log("remove channel");
      yield put(
        firestoreActions.closeChannel({ channel: "streamingComments" })
      );
    }

    const { eventId, videoId, eventStart, player, isEdge } = action.payload;
    if (!eventStart) {
      return;
    }
    const {
      auth: { user },
      event: { displayEvent },
      streaming: { commentShardId },
    }: Store = yield select();

    // initialize reserved comments
    yield put(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      streamingActions.fetchDisplayCommentsByTimeUpdate.done([] as any)
    );

    // HACK: isEdgeの判定時に、最新から10秒の以内にシークする場合には, player timeupdate eventにaddEventListenerしていない
    // そのため、isEdgeがtrueの時には、+30s分余分にコメントを取得しておく
    const additionalSeconds = isEdge ? 30 : 0;
    const end =
      eventStart.seconds + Math.floor(player.currentTime) + additionalSeconds;
    const msgRef = getCommentCollectionPath(
      eventId,
      videoId,
      displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
      commentShardId.value
    );

    let {
      streaming: {
        streamingSettings: { banUserIds },
      },
    }: Store = yield select();
    if (banUserIds == null) {
      try {
        // ブロックユーザー一覧が未取得だった場合はここで事前に取得
        const msgCollectionParentRef = msgRef.slice(0, -5); // remove '/msgs'
        const data: EventChatInfo | null = yield fetchFirestoreDocument(
          msgCollectionParentRef
        );
        yield put(
          streamingActions.fetchBanUserIds.done({
            params: action.payload,
            result: data?.banUserIds ?? [],
          })
        );
        banUserIds = data?.banUserIds ?? [];
      } catch (error) {
        console.error("Error fetching banUserIds:", error);
      }
    }

    // 表示用のメッセージを取得する
    const snapshot = firebase
      .firestore()
      .collection(msgRef)
      .where("timestamp", "<", new Date(end * 1000))
      .orderBy("timestamp", "desc")
      .limit(DISPLAY_COMMENT_NUM)
      .get();
    const newArray: UserMessage[] =
      yield fetchFirestoreCollectionBySnapshot(snapshot);
    console.log(`handleSeek: ${newArray.length}`);
    const comments = filterBANCommentWithoutSelfComment(
      newArray.length > 0 ? newArray.reverse() : [],
      banUserIds ?? [],
      user.uid
    );
    yield put(streamingActions.handleSeekEvents.done(comments as never));
  } catch (error) {
    console.error(error);
  }
}

function* fetchDisplayCommentsByTimeUpdate(action: {
  payload: ReqFetchDisplayCommentsByTimeUpdate;
}) {
  const { currentTime: _c, eventStart, eventId, videoId } = action.payload;
  const currentTime = Math.floor(_c);

  const {
    auth: { user },
    event: { displayEvent },
    streaming: {
      reservedComments,
      displayComments,
      commentShardId,
      pauseCommentFetch,
      streamingSettings: { banUserIds },
    },
  }: Store = yield select();

  if (pauseCommentFetch) {
    return;
  }

  const startEvent = eventStart.seconds;

  try {
    const newComments = reservedComments.filter((el) => {
      const commentTime = el.timestamp.seconds - startEvent;
      return commentTime < currentTime;
    });

    // 更新がない時は余計なレンダリングをしないように飛ばす
    if (newComments.length > 0) {
      const commentsToDisplay = displayComments.concat(newComments);
      const begin = Math.max(commentsToDisplay.length - DISPLAY_COMMENT_NUM, 0);
      yield put(
        streamingActions.fetchDisplayComments.done(
          commentsToDisplay.slice(begin) as never
        )
      );
    }

    // remove displayed comment
    const _reservedComments = reservedComments.filter(
      (el) => el.timestamp.seconds - startEvent >= currentTime
    );

    // store next comment
    if (_reservedComments.length === 0) {
      const msgRef = getCommentCollectionPath(
        eventId,
        videoId,
        displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
        commentShardId.value
      );
      // const snapshot = fetchFirestoreDisplayMessage(msgRef, startEvent + currentTime, COMMENT_RECEIVER_SIZE)
      const snapshot = firebase
        .firestore()
        .collection(msgRef)
        .orderBy("timestamp")
        .where("timestamp", ">", new Date((startEvent + currentTime) * 1000))
        .limit(ARCHIVE_COMMENT_FETCH_SIZE)
        .get();
      const newArray: UserMessage[] =
        yield fetchFirestoreCollectionBySnapshot(snapshot);
      yield put(
        streamingActions.fetchDisplayCommentsByTimeUpdate.done(
          filterBANCommentWithoutSelfComment(
            newArray,
            banUserIds ?? [],
            user.uid
          ) as never
        )
      );
    } else {
      yield put(
        streamingActions.fetchDisplayCommentsByTimeUpdate.done(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          _reservedComments as any
        )
      );
    }
  } catch (error) {
    console.error(error);
  }
}

function* postComment(action: { payload: ReqPostComment }) {
  try {
    const {
      auth: { user },
    }: Store = yield select();
    if (!user?.uid) {
      return;
    }

    const { eventId, videoId, isGift, postData, itemId, mutatePAW } =
      action.payload;

    const data = {
      ...postData,
      timestamp: firebase.firestore.FieldValue.serverTimestamp(),
    };

    const {
      event: { displayEvent },
      streaming: { commentShardId },
    }: Store = yield select();

    const msgRef = getCommentCollectionPath(
      eventId,
      videoId,
      displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
      commentShardId.value
    );
    yield firebase.firestore().collection(msgRef).add(data);

    // if payed gift, use paw after send comment
    // pawを使用してギフトが送られない場合の保険
    if (isGift) {
      yield put(
        purchaseActions.usePAW.started({
          suppressMsg: true,
          eid: eventId,
          itemId,
          freePrice: 0,
          paidPrice: postData.amount,
          mutatePAW,
        })
      );
    }

    yield put(
      streamingActions.postComment.done({
        params: action.payload,
        result: {
          ...postData,
          timestamp: firebase.firestore.Timestamp.now(),
        },
      })
    );
  } catch (error) {
    console.error(error);
  }
}

function* fetchNgWords(action: { payload: ReqNgWords }) {
  const { eventId } = action.payload;
  try {
    const msgRef = `/chat/${eventId}`;
    const data: EventChatInfo = yield fetchFirestoreDocument(msgRef);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    yield put(streamingActions.fetchNgWords.done((data.ngWords as any) || []));
  } catch (error) {
    console.error(error);
  }
}

function* fetchBanUserIds(action: { payload: ReqFetchBANComments }) {
  const { eventId, videoId } = action.payload;

  const {
    firestore: { channels },
  }: Store = yield select();
  if (isRegisteredChannel(channels, "banUserIds")) {
    yield put(firestoreActions.closeChannel({ channel: "banUserIds" }));
  }

  try {
    const {
      streaming: { commentShardId },
      event: {
        displayEvent: { streamingInfo },
      },
    }: Store = yield select();

    const msgCollectionParentRef = getCommentCollectionPath(
      eventId,
      videoId,
      streamingInfo?.optionSettings?.isCommentShardingEnabled,
      commentShardId.value
    ).slice(0, -5); // remove '/msgs'

    // @ts-expect-error TS7057
    const channel = yield call(banUserIdsChannel, msgCollectionParentRef);
    yield put(firestoreActions.addChannel({ ...channel, name: "banUserIds" }));
    while (true) {
      const data: string[] = yield take(channel);
      const {
        auth: { user },
        streaming: { displayComments },
      }: Store = yield select();
      const newDisplayComments = displayComments.map((c) => ({
        ...c,
        // 自分以外のブロックされたユーザーのコメントを全てBANされた扱いにする
        isBan: c.isBan || (c.uid !== user.uid && data.includes(c.uid)),
      }));
      yield put(
        streamingActions.fetchBANComments.done(newDisplayComments as never)
      );
      yield put(
        streamingActions.fetchBanUserIds.done({
          params: action.payload,
          result: data,
        })
      );
    }
  } catch (error) {
    console.error(error);
  }
}
function banUserIdsChannel(ref: string) {
  return eventChannel((emitter) => {
    return firebase
      .firestore()
      .doc(ref)
      .onSnapshot(
        (snapshot) => emitter(snapshot.data()?.banUserIds ?? []),
        console.error
      );
  });
}

/**
 * onSnapshotで取得した新規コメントをバッファーに投入するSaga
 */
function* watchOnSnapshotSaga(msgRef: string) {
  // @ts-expect-error TS7057
  const channel = yield call(newStreamingCommentChannel, msgRef);

  try {
    while (true) {
      // @ts-expect-error TS7057
      const update = yield take(channel);
      yield put(streamingActions.bufferedCommentPut(update));
    }
  } finally {
    // channelのunsubscribe関数を呼び出す
    channel.close();
  }
}

/**
 * バッファーを監視し、1秒ごとにまとめて処理するSaga
 */
function* processBatchesSaga() {
  while (true) {
    yield take(streamingActions.bufferedCommentPut);

    try {
      const {
        streaming: { bufferedComments, displayComments, pauseCommentFetch },
      }: Store = yield select();

      if (pauseCommentFetch) {
        continue;
      }

      let isDirty = false;
      bufferedComments.forEach((change) => {
        if (
          change.type === "added" &&
          !displayComments.some((comment) => comment._id === change.doc.id)
        ) {
          // added かつ重複idでなければ追加
          isDirty = true;
          displayComments.push({
            ...(change.doc.data() as UserMessage),
            _id: change.doc.id,
          });
        } else if (change.type === "modified") {
          // 自分の書き込みの場合は`modified`を通る
          const index = displayComments.findIndex(
            (comment) => comment._id === change.doc.id
          );
          if (index === -1) {
            // modified かつ重複idでなければ追加
            isDirty = true;
            displayComments.push({
              ...(change.doc.data() as UserMessage),
              _id: change.doc.id,
            });
          } else {
            const newDoc = {
              ...(change.doc.data() as UserMessage),
              _id: change.doc.id,
            };
            if (
              JSON.stringify(displayComments[index]) !== JSON.stringify(newDoc)
            ) {
              // modified かつ重複idで差分があるなら同じidに上書き
              isDirty = true;
              displayComments.splice(index, 1, {
                ...(change.doc.data() as UserMessage),
                _id: change.doc.id,
              });
            }
          }
        }
        // `change.type: removed` はonSnapshotのlimit対象から外れたドキュメントのことも指すため削除されたコメントとは限らない
        // ここでは判定できないので何もしない。 ref: https://qiita.com/mktu/items/17a993f675ccd6d17aed
      });

      // コメントが変化した場合のみソートして更新
      if (isDirty) {
        // `.orderBy("timestamp", "desc")` で取得している関係でソートしている
        // eslint-disable-next-line no-confusing-arrow
        displayComments.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));

        yield put(
          streamingActions.newFetchDisplayComments.done({
            params: undefined as unknown as ReqFetchDisplayComments, // paramsは必要ないので無を渡す
            result: displayComments.slice(-NEW_DISPLAY_COMMENT_NUM),
          })
        );
      }

      yield put(streamingActions.bufferedCommentClear());

      // 1000±500msのランダムな遅延を挟む
      yield delay(500 + Math.random() * 1000);
    } catch (error) {
      console.error("Batch processing failed:", error);
    }
  }
}

// BANされたコメントでも、自分のコメントは表示する
function filterBANCommentWithoutSelfComment(
  comments: UserMessage[],
  banUserIds: string[],
  userId: string
) {
  return comments.filter(
    (c) =>
      c.uid === userId ||
      !((c.hasOwnProperty("isBan") && c.isBan) || banUserIds.includes(c.uid))
  );
}

// ポーリングを実行するワーカーSaga
function* pollingCommentTask(msgRef: string) {
  const {
    auth: { user },
    streaming: {
      streamingSettings: { banUserIds },
    },
  }: Store = yield select();

  // 初回取得
  const snapshot = firebase
    .firestore()
    .collection(msgRef)
    .orderBy("timestamp", "desc")
    .limit(DISPLAY_COMMENT_NUM)
    .get();
  const newArray: UserMessage[] =
    yield fetchFirestoreCollectionBySnapshot(snapshot);
  const comments = filterBANCommentWithoutSelfComment(
    newArray.length > 0 ? newArray.reverse() : [],
    banUserIds ?? [],
    user.uid
  );
  yield put(
    streamingActions.startCommentPolling.done({
      params: undefined as unknown as ReqFetchDisplayComments, // paramsは必要ないので無を渡す
      result: comments,
    })
  );
  const lastComment = comments[comments.length - 1];
  if (lastComment) {
    yield put(
      streamingActions.setLastCommentedTimestamp(lastComment.timestamp)
    );
  }

  while (true) {
    // 1秒待機
    yield delay(1000);

    try {
      const {
        auth: { user },
        streaming: {
          displayComments,
          lastCommentedTimestamp,
          pauseCommentFetch,
          streamingSettings: { banUserIds },
        },
      }: Store = yield select();

      if (pauseCommentFetch) {
        continue;
      }

      let query = firebase
        .firestore()
        .collection(msgRef)
        .orderBy("timestamp", "desc");
      if (lastCommentedTimestamp) {
        query = query.where("timestamp", ">", lastCommentedTimestamp);
      }
      const snapshot = query
        .limit(DISPLAY_COMMENT_NUM) // コメント量が多い場合は画面に表示しきれないので取得量を画面上限分までに制限
        .get();
      const newComments: UserMessage[] =
        yield fetchFirestoreCollectionBySnapshot(snapshot);
      const filteredComments = filterBANCommentWithoutSelfComment(
        newComments,
        banUserIds ?? [],
        user.uid
      );

      if (filteredComments.length > 0) {
        type LocalComment = UserMessage & { isLocalChange: boolean };
        // 自分のコメントが含まれる場合があるのでポーリングで取得したデータを優先して重複排除
        const omitDuplicationComments = (displayComments as LocalComment[])
          .filter((c) => !c.isLocalChange) // isLocalChangeがtrueのものは除外
          .filter((c) => !filteredComments.some((nc) => nc._id === c._id));
        const newDisplayComments = omitDuplicationComments
          .concat(filteredComments.reverse() as LocalComment[])
          .slice(-DISPLAY_COMMENT_NUM)
          // eslint-disable-next-line no-confusing-arrow
          .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));
        yield put(
          streamingActions.startCommentPolling.done({
            params: undefined as unknown as ReqFetchDisplayComments, // paramsは必要ないので無を渡す
            result: newDisplayComments,
          })
        );
        const lastComment = filteredComments[filteredComments.length - 1];
        if (lastComment) {
          yield put(
            streamingActions.setLastCommentedTimestamp(lastComment.timestamp)
          );
        }
      }
    } catch (error) {
      console.error("Comment polling failed:", error);
      // エラー発生時は少し長めに待機してからリトライする
      yield delay(5000);
    }
  }
}

// ポーリングの開始と停止を監視するウォッチャーSaga
function* watchPollingComment(
  action: ReturnType<typeof streamingActions.startCommentPolling.started>
) {
  const { eventId, videoId } = action.payload;

  const {
    event: { displayEvent },
    streaming: {
      commentShardId,
      streamingSettings: { banUserIds },
    },
  }: Store = yield select();

  const msgRef = getCommentCollectionPath(
    eventId,
    videoId,
    displayEvent?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
    commentShardId.value
  );

  if (banUserIds == null) {
    try {
      // ブロックユーザー一覧が未取得だった場合はここで事前に取得
      const msgCollectionParentRef = msgRef.slice(0, -5); // remove '/msgs'
      const data: EventChatInfo | null = yield fetchFirestoreDocument(
        msgCollectionParentRef
      );
      yield put(
        streamingActions.fetchBanUserIds.done({
          params: action.payload,
          result: data?.banUserIds ?? [],
        })
      );
    } catch (error) {
      console.error("Error fetching banUserIds:", error);
    }
  }

  while (true) {
    // raceエフェクトでpollingCommentTaskとstopCommentPollingアクションを競合させる
    // stopCommentPollingがdispatchされると、pollingCommentTaskは自動的にキャンセルされる
    // @ts-expect-error TS7057
    const result = yield race([
      call(pollingCommentTask, msgRef),
      take(streamingActions.stopCommentPolling),
    ]);

    /**
     * stop時には,[null, { "type": 'streaming/stopCommentPolling' }] このような形でresultが返る
     */
    try {
      if (result && result[1] !== null) {
        break;
      }
    } catch (error) {
      console.error("Error in polling comment watcher:", error);
    }
  }
}

/***
 * chatVersion null
 *   shardIdをコメントに訪れるたびに生成する
 * chatVersion 2
 *   初回でshardIdを生成し、以降はshardIdをDBから取得する
 */
type CHAT_VERSION = null | 2;
export type ReqFetchShardIdArgs = {
  chatVersion: CHAT_VERSION;
  // 設定されているshard数
  nCommentShards?: number;
  isCommentShardingEnabled: boolean;
  userId: string;
  eventId: string;
  videoId: string;
};

const generateShardId = (nCommentShards?: number): number => {
  const nShards = nCommentShards || 0; // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
  return Math.floor(Math.random() * nShards);
};

const getCommentShardId = async (
  params: ReqFetchShardIdArgs
): Promise<number | undefined> => {
  const query = firebase
    .firestore()
    .collection(`/users/${params.userId}/userChatRooms`)
    .where("eventId", "==", params.eventId)
    .where("videoId", "==", params.videoId)
    .limit(1);
  const userChatRoom = await query.get();
  if (userChatRoom.empty) return undefined;
  // @ts-expect-error TS2532
  return userChatRoom.docs[0].data().shardId;
};

const createChatRoom = async (
  params: ReqFetchShardIdArgs & {
    shardId: number;
  }
): Promise<void> => {
  const docId = `${params.eventId}-${params.videoId}-${params.shardId}`;
  const ref = `/users/${params.userId}/userChatRooms/${docId}` as const;
  await setFirestoreDocument(ref, {
    videoId: params.videoId,
    eventId: params.eventId,
    shardId: params.shardId,
  });
};

const getOrCreateCommentShardId = async (
  params: ReqFetchShardIdArgs
): Promise<number> => {
  // v2以前は変わらずランダムでshardIdを生成する
  if (params.chatVersion !== 2) return generateShardId(params.nCommentShards);

  // shardIdが設定されている場合はそれを返す
  const storedCommentShardId = await getCommentShardId(params);
  // NOTE: 0のshardIdがある
  if (Number.isSafeInteger(storedCommentShardId)) {
    return storedCommentShardId as number;
  }

  // shardIdが設定されていない場合は新しく生成しroom情報として保存する
  const shardId = generateShardId(params.nCommentShards);
  await createChatRoom({ ...params, shardId });
  return shardId;
};

function* fetchCommentShardId(action: { payload: ReqFetchShardIdArgs }) {
  if (!action.payload.isCommentShardingEnabled) {
    put(
      streamingActions.fetchCommentShardId.done({
        params: undefined,
        result: null,
      })
    );
  }

  const shardId: number = yield getOrCreateCommentShardId(action.payload);
  yield put(
    streamingActions.fetchCommentShardId.done({
      params: undefined,
      result: shardId,
    })
  );
}
